import { type ApiFetchContext, apiFetch } from "../client";
import type { BackendLang } from "../headers";
import type {
  CountryVoRaw,
  DomesticTrack,
  EstimateCountry,
  EstimateCountryGroups,
  EstimateInput,
  LogisticsCategory,
  LogisticsCategoryRaw,
  LogisticsRoute,
  LogisticsRouteRaw,
  LogisticsTraceOldRaw,
  ServiceItem,
  ServiceItemRaw,
} from "../types/logistics";

export interface LangCurrencyOpts {
  lang?: BackendLang;
  /** Display currency for backend price conversion (drives `estTotalExpressCost`). */
  currency?: "RUB" | "CNY" | "USD";
}

function headerInit(opts: LangCurrencyOpts) {
  return {
    ...(opts.lang === undefined ? {} : { lang: opts.lang }),
    ...(opts.currency === undefined ? {} : { currency: opts.currency }),
  };
}

const toNumberOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/\s/g, ""));
  return Number.isFinite(n) ? n : null;
};

const isYes = (v: string | boolean | null | undefined): boolean =>
  v === true || v === "Y" || v === "y" || v === "1";

function normalizeCountry(raw: CountryVoRaw): EstimateCountry {
  return {
    code: raw.countryCode,
    nameCn: raw.countryNameCn ?? raw.countryCode,
    nameEn: raw.countryNameEn ?? raw.countryCode,
    nameRu: raw.countryNameRu ?? null,
    flagUrl: raw.flagUrl ?? null,
  };
}

/**
 * GET /base/logistics/country/list — destination countries grouped into
 * HOT / alphabet buckets / China. Public (no auth). The estimate's `country`
 * field is the returned `countryCode`.
 */
export async function getEstimateCountries(
  ctx: ApiFetchContext,
  opts: LangCurrencyOpts = {},
): Promise<EstimateCountryGroups> {
  const raw = await apiFetch<Record<string, CountryVoRaw[]>>(
    ctx,
    "/base/logistics/country/list",
    headerInit(opts),
  );
  const out: EstimateCountryGroups = {};
  for (const [group, list] of Object.entries(raw ?? {})) {
    out[group] = (list ?? []).map(normalizeCountry);
  }
  return out;
}

function normalizeCategory(raw: LogisticsCategoryRaw): LogisticsCategory {
  return {
    code: raw.code ?? raw.key ?? raw.value ?? "",
    title: raw.title ?? "",
    children: (raw.children ?? []).map(normalizeCategory),
  };
}

/** GET /base/logistics/category/list — shippable product-category tree. Public. */
export async function getLogisticsCategories(
  ctx: ApiFetchContext,
  opts: LangCurrencyOpts = {},
): Promise<LogisticsCategory[]> {
  const raw = await apiFetch<LogisticsCategoryRaw[]>(
    ctx,
    "/base/logistics/category/list",
    headerInit(opts),
  );
  return (raw ?? []).map(normalizeCategory);
}

export function normalizeRoute(raw: LogisticsRouteRaw): LogisticsRoute {
  return {
    routeId: raw.routeId ?? "",
    channelId: raw.channelId ?? null,
    brandLogoUrl: raw.brandLogoUrl ?? null,
    name: raw.name ?? "",
    description: raw.description ?? null,
    shippingType: raw.shippingType ?? null,
    minDeliveredDays: raw.minDeliveredDays ?? null,
    maxDeliveredDays: raw.maxDeliveredDays ?? null,
    deliveredTimeUnit: raw.deliveredTimeUnit ?? null,
    calcWeightGrams: raw.calcWeight ?? null,
    goodsWeightGrams: raw.goodsWeight ?? null,
    volumeWeightGrams: raw.volumeWeight ?? null,
    weightType: raw.weightType ?? null,
    tips: raw.tips ?? null,
    weightRuleDesc: raw.weightRuleDesc ?? null,
    totalCostDisplay: raw.estTotalExpressCost ?? null,
    cargoCostDisplay: raw.estCargoExpressCost ?? null,
    totalCostNumber: toNumberOrNull(raw.estTotalExpressCost),
    cargoCostNumber: toNumberOrNull(raw.estCargoExpressCost),
    recommended: isYes(raw.recmd),
    recmdReason: raw.recmdReason ?? null,
    hasInsurance: isYes(raw.insure),
    isTriangle: isYes(raw.triangle),
    tariff: raw.tariff ?? null,
    tariffText:
      raw.tariffText && raw.tariffText !== "untype" && raw.tariffText !== "无类型"
        ? raw.tariffText
        : null,
    // Backend default is `true`; only an explicit `false` means not carriable.
    carriable: raw.isCarriable !== false,
    rejectReason: raw.rejectReason ?? null,
    // `selectable` only ships when includeUnselectable was requested; absent → true.
    selectable: raw.selectable !== false,
    rejectReasonKey: raw.rejectReasonKey ?? null,
    maxWeightKg: toNumberOrNull(raw.maxWeightKg),
    minWeightKg: toNumberOrNull(raw.minWeightKg),
    chargeableWeightKg: raw.calcWeight != null ? raw.calcWeight / 1000 : null,
    sizeLimitExp: raw.sizeLimitExp ?? null,
  };
}

/**
 * POST /logistics/estimate/international2 — international freight estimate.
 * Mirrors the legacy aps-web `estimateInternational` call (it also used the
 * `…/international2` variant). Money fields come back already converted to the
 * `currency` header; we pass them through as display strings.
 *
 * Public — no auth required (the freight calculator must work signed-out).
 */
export async function estimateInternational(
  ctx: ApiFetchContext,
  input: EstimateInput,
  opts: LangCurrencyOpts = {},
): Promise<LogisticsRoute[]> {
  const raw = await apiFetch<LogisticsRouteRaw[]>(ctx, "/logistics/estimate/international2", {
    method: "POST",
    ...headerInit(opts),
    body: {
      country: input.country,
      weight: input.weightGrams,
      length: input.lengthCm ?? undefined,
      width: input.widthCm ?? undefined,
      high: input.heightCm ?? undefined,
      categoryList: input.categoryList ?? [],
      ...(input.includeUnselectable ? { includeUnselectable: true } : {}),
    },
  });
  return (raw ?? []).map(normalizeRoute);
}

function normalizeDomesticTrack(raw: LogisticsTraceOldRaw): DomesticTrack {
  const steps = (raw.logisticsSteps ?? [])
    .map((s) => ({
      acceptTime: s.acceptTime ?? "",
      remarks: s.remarks ?? "",
      status: s.status ?? "",
      location: s.location ?? "",
      carrier: s.carrier ?? "",
      time: s.time ?? "",
      raw: s.raw ?? "",
      cleanedText: s.cleanedText ?? null,
    }))
    // Drop fully-empty rows; keep backend fallback (it has `remarks`).
    .filter((s) => s.acceptTime !== "" || s.remarks !== "" || s.status !== "");

  const aggregatedSteps =
    raw.aggregatedSteps != null
      ? raw.aggregatedSteps.map((a) => ({
          status: a.status ?? "",
          location: a.location ?? "",
          carrier: a.carrier ?? "",
          time: a.time ?? "",
          cleanedText: a.cleanedText ?? null,
          count: a.count ?? 1,
        }))
      : null;

  return { logisticsId: raw.logisticsId ?? "", steps, aggregatedSteps };
}

/**
 * GET /logistics/track/domestic?expressNo=<waybill> — China-side seller-waybill
 * trace. Returns one `LogisticsTraceOldVo` per waybill (usually a single
 * element). Events are de-identified + translated server-side; an empty trace
 * comes back as a single fallback step. Public-ish but tied to the order, so we
 * pass the auth token like the other order/express reads.
 */
export async function trackDomestic(
  ctx: ApiFetchContext,
  authToken: string,
  expressNo: string,
  opts: LangCurrencyOpts = {},
): Promise<DomesticTrack[]> {
  const raw = await apiFetch<LogisticsTraceOldRaw[]>(
    ctx,
    `/logistics/track/domestic?expressNo=${encodeURIComponent(expressNo)}`,
    { authToken, ...headerInit(opts) },
  );
  return (raw ?? []).map(normalizeDomesticTrack);
}

function normalizeServiceItem(raw: ServiceItemRaw): ServiceItem {
  // `charge` is already in the request currency; backend amounts are major
  // units, so store as integer minor units to feed the dual formatter.
  const charge = toNumberOrNull(raw.charge) ?? 0;
  const isFree = raw.isCharge === false || charge <= 0;
  return {
    id: raw.id ?? "",
    title: raw.name ?? "",
    description: raw.description ?? null,
    priceMinor: Math.round(charge * 100),
    chargeUnit: raw.chargeUnit ?? null,
    isFree,
    type: raw.type ?? null,
  };
}

/** GET /logistics/service/item/list — enabled warehouse packing/value services. */
export async function getServiceItems(
  ctx: ApiFetchContext,
  opts: LangCurrencyOpts = {},
): Promise<ServiceItem[]> {
  const raw = await apiFetch<ServiceItemRaw[]>(
    ctx,
    "/logistics/service/item/list",
    headerInit(opts),
  );
  return (raw ?? []).map(normalizeServiceItem);
}
